/**
 * Generator runtime shared by the main-thread scheduler and the generator worker.
 *
 * This module is the only place that actually executes generator `build` /
 * `rebuild` functions. The store submits typed requests to the worker; the
 * worker calls `executeGeneratorRequest()` and returns the result. Keeping the
 * execution surface here prevents a store-side direct-call fallback path from
 * creeping back in.
 */

import { cloneSceneObject } from "../../app/persistence/cloneSceneObject.ts";
import {
  recomputeLoopNormals,
  resolveAutoSmoothAngle,
} from "../../wasm/normals.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import type { SceneObject } from "../types.ts";
import { getGenerator } from "./registry.ts";
import type {
  GeneratorBuildContext,
  GeneratorListItem,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";

export interface GeneratorMeshShading {
  shading: SceneObject["shading"];
  autoSmoothAngle?: number;
}

export interface GeneratorRequest {
  mode: "build" | "rebuild";
  generatorId: string;
  params: GeneratorParams;
  sceneObjectsById: Record<string, SceneObject>;
  meshShading?: GeneratorMeshShading;
  defaultChildShading?: GeneratorMeshShading;
  childShadingByRoleId?: Record<string, GeneratorMeshShading>;
}

export interface GeneratorResult {
  output: GeneratorOutput;
  geometrySignature?: string;
}

function makeBuildContext(
  sceneObjectsById: Record<string, SceneObject>,
): GeneratorBuildContext {
  return {
    resolveObject: (id) => (id ? (sceneObjectsById[id] ?? null) : null),
  };
}

function collectReferencedObjectIdsFromSpecs(
  specs: readonly GeneratorParamSpec[],
  params: GeneratorParams,
  out: Set<string>,
): void {
  for (const spec of specs) {
    if (spec.type === "section") {
      collectReferencedObjectIdsFromSpecs(spec.params, params, out);
      continue;
    }

    const value = params[spec.id];
    if (spec.type === "objectRef") {
      if (typeof value === "string" && value.length > 0) out.add(value);
      continue;
    }

    if (spec.type === "list" && Array.isArray(value)) {
      for (const item of value as readonly GeneratorListItem[]) {
        const itemType = spec.itemTypes.find(
          (candidate) => candidate.id === item.itemType,
        );
        if (!itemType) continue;
        collectReferencedObjectIdsFromSpecs(
          itemType.paramSpecs,
          item.params,
          out,
        );
      }
    }
  }
}

/**
 * Collect every SceneObject id currently referenced by a generator params record.
 *
 * The result is schema-driven and recursive, so new generators and new nested
 * list item types automatically participate without any store-side special
 * casing.
 */
export function collectReferencedObjectIds(
  specs: readonly GeneratorParamSpec[],
  params: GeneratorParams,
): Set<string> {
  const out = new Set<string>();
  collectReferencedObjectIdsFromSpecs(specs, params, out);
  return out;
}

/**
 * Build a worker request snapshot containing only the SceneObjects that the
 * generator currently references via `objectRef` params.
 *
 * The objects are deep-cloned before transfer so the worker can take
 * ownership of their typed-array buffers without detaching the live store's
 * canonical geometry.
 */
export function buildGeneratorRequest(
  generatorId: string,
  specs: readonly GeneratorParamSpec[],
  params: GeneratorParams,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  options: {
    mode: "build" | "rebuild";
    meshShading?: GeneratorMeshShading;
    defaultChildShading?: GeneratorMeshShading;
    childShadingByRoleId?: Record<string, GeneratorMeshShading>;
    extraSceneObjects?: Iterable<SceneObject>;
  },
): GeneratorRequest {
  const sceneObjectsById: Record<string, SceneObject> = {};
  const referencedIds = collectReferencedObjectIds(specs, params);
  for (const id of referencedIds) {
    const sceneObject = sceneObjects.get(id);
    if (sceneObject) sceneObjectsById[id] = cloneSceneObject(sceneObject);
  }
  if (options.extraSceneObjects) {
    for (const sceneObject of options.extraSceneObjects) {
      sceneObjectsById[sceneObject.id] = cloneSceneObject(sceneObject);
    }
  }
  return {
    mode: options.mode,
    generatorId,
    params,
    sceneObjectsById,
    meshShading: options.meshShading,
    defaultChildShading: options.defaultChildShading,
    childShadingByRoleId: options.childShadingByRoleId,
  };
}

function hydrateGeometryLoopNormals(
  geometry: PrimitiveGeometry,
  shading: GeneratorMeshShading | undefined,
): PrimitiveGeometry {
  if (!shading) return geometry;
  return {
    ...geometry,
    loopNormals: recomputeLoopNormals(
      geometry.vertices,
      geometry.topology,
      resolveAutoSmoothAngle(shading.shading, shading.autoSmoothAngle),
    ),
  };
}

function hydrateOutputLoopNormals(
  output: GeneratorOutput,
  request: GeneratorRequest,
): GeneratorOutput {
  if (output.kind === "mesh") {
    return {
      ...output,
      geometry: hydrateGeometryLoopNormals(
        output.geometry,
        request.meshShading,
      ),
    };
  }
  return {
    ...output,
    children: output.children.map((child) => ({
      ...child,
      geometry: hydrateGeometryLoopNormals(
        child.geometry,
        request.childShadingByRoleId?.[child.roleId] ??
          request.defaultChildShading,
      ),
    })),
  };
}

/**
 * Execute one generator request.
 *
 * This is worker-owned runtime logic. Callers outside the worker should submit
 * a `GeneratorRequest` through `generatorWorkerClient.ts` rather than invoking
 * this directly.
 */
export function executeGeneratorRequest(
  request: GeneratorRequest,
): GeneratorResult {
  const generator = getGenerator(request.generatorId);
  if (!generator) {
    throw new Error(`Unknown generator "${request.generatorId}".`);
  }
  const ctx = makeBuildContext(request.sceneObjectsById);
  const output =
    request.mode === "build"
      ? generator.build(request.params, ctx)
      : generator.rebuild(request.params, ctx);
  return {
    output: hydrateOutputLoopNormals(output, request),
    geometrySignature: generator.geometrySignature?.(request.params, ctx),
  };
}
